import React, { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { Visualization } from '../../../../rendering/Visualizer';
import { getGlobalState, useGlobalBucket } from '@gui/store/persistent/persistentStore';
import { alpha, useTheme } from '@mui/material/styles';

interface CompassProps {
    viewer: Visualization;
}

// CSS
import '../../style.css';
import { CameraMode } from '@visualizer/types';

export const OverlayCompass: React.FC<CompassProps> = (props) => {
    const visible = useGlobalBucket((s) => s.overlays.compassVisible);
    const imgRef = useRef<HTMLImageElement>(null);
    const sphRef = useRef(new THREE.Spherical());
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';

    // Drawer/column
    const columnBg = alpha(theme.palette.background.default, isLight ? 0.45 : 0.3);
    const columnBlurPx = 10;

    // Panel card layer
    const panelBg = alpha(theme.palette.background.paper, isLight ? 0.26 : 0.5);
    const panelBorder = isLight ? alpha(theme.palette.background.paper, 0.6) : alpha(theme.palette.common.white, 0.08);
    const panelColor = theme.palette.text.primary;

    const radToDeg = useCallback((radians: number): number => {
        return radians * (180 / Math.PI);
    }, []);

    const handleCompassClick = useCallback(() => {
        const v = props.viewer;
        if (!v) return;

        const camType = getGlobalState().ambient.currentCamera.type as CameraMode;
        v.cameraHelper.resetRotation(camType); // your north-facing default
    }, [props.viewer]);

    const updateCompass = useCallback(() => {
        if (!imgRef.current) return;

        // Update compass rotation
        const dir = new THREE.Vector3();
        props.viewer.cameraHelper.getActiveCamera(getGlobalState().ambient.currentCamera.type).getWorldDirection(dir);
        sphRef.current.setFromVector3(dir);
        imgRef.current.style.transform = `rotate(${radToDeg(sphRef.current.theta) - 180}deg)`;
    }, [radToDeg]);

    useEffect(() => {
        const controls = props.viewer?.cameraHelper.getOrbitControls();
        if (!controls) return;

        const handleChange = () => {
            updateCompass();
        };

        controls.addEventListener('change', handleChange);

        return () => {
            controls.removeEventListener('change', handleChange);
        };
    }, [updateCompass, props.viewer]);

    if (!visible) return null;

    // ------------------------- UI ----------------------------------------------
    return (
        <div
            style={{
                position: 'fixed',
                zIndex: 1,
                pointerEvents: 'none',
                left: 'calc(16px + var(--left-drawer-visible, 0px))',
                bottom: 16,
            }}
        >
            <div
                style={{
                    display: 'inline-block',
                    borderRadius: 28,
                    background: columnBg,
                    backdropFilter: `blur(${columnBlurPx}px)`,
                    WebkitBackdropFilter: `blur(${columnBlurPx}px)`,
                    padding: 0,
                }}
            >
                <div
                    className="data-panel overlay-compass"
                    onClick={handleCompassClick}
                    style={{
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                        background: panelBg,
                        border: `1px solid ${panelBorder}`,
                        color: panelColor,
                        borderRadius: '50%',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                    }}
                >
                    <img
                        ref={imgRef}
                        src="/visualizer/assets/icons/compass.png"
                        alt="Compass"
                        width={48}
                        height={48}
                        style={{
                            userSelect: 'none',
                            pointerEvents: 'none',
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default OverlayCompass;
